import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon, Close as CloseIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import ConfirmDialog from "../../components/layout/ConfirmDialog";
import { useConfirmDialog } from "../../hooks/useConfirmDialog";
import supplierAPI, { Supplier, SupplierPayload } from "../../services/supplierAPI";
import reportAPI from "../../services/reportAPI";
import { rowsToCsv } from "../../utils/csvExport";
import { useRBAC } from "../../hooks/useRBAC";
import { useTradeTerminology } from "../../utils/tradeTerminology";

const emptyForm = (): SupplierPayload => ({
  name: "",
  contactPerson: "",
  phone: "",
  email: "",
  taxId: "",
  city: "",
  state: "",
  address: "",
  isActive: true,
});

const SuppliersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { terms } = useTradeTerminology();
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [form, setForm] = useState<SupplierPayload>(emptyForm());
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["suppliers", page, rowsPerPage, search, statusFilter],
    queryFn: () =>
      supplierAPI.getSuppliers({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        search: search || undefined,
        isActive:
          statusFilter === "active"
            ? true
            : statusFilter === "inactive"
            ? false
            : undefined,
      }),
  });

  const { mutate: save, isPending: saving } = useMutation({
    mutationFn: async () => {
      if (editing) {
        return supplierAPI.updateSupplier(editing.id, form);
      }
      return supplierAPI.createSupplier(form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["suppliersAll"] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      setAlert({
        type: "success",
        message: editing ? "Supplier updated." : "Supplier created.",
      });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to save supplier",
      });
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: string) => supplierAPI.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["suppliersAll"] });
      setAlert({ type: "success", message: "Supplier deleted." });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete supplier",
      });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: Supplier) => {
    setEditing(row);
    setForm({
      name: row.name,
      contactPerson: row.contactPerson || "",
      phone: row.phone || "",
      email: row.email || "",
      taxId: row.taxId || "",
      city: row.city || "",
      state: row.state || "",
      address: row.address || "",
      isActive: row.isActive,
    });
    setOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      setAlert({ type: "error", message: "Name is required." });
      return;
    }
    save();
  };

  async function handleTallyExport() {
    setExporting(true);
    try {
      const data = await reportAPI.getTallyPartiesExport();
      const csv = rowsToCsv(data.rows || []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tally_parties.csv";
      link.click();
      URL.revokeObjectURL(url);
      setAlert({ type: "success", message: "Tally party master CSV downloaded." });
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to export parties",
      });
    } finally {
      setExporting(false);
    }
  }

  return (
    <Box>
      <PageHeader
        title={terms.suppliersLabel}
        subtitle="Party master for purchases, debit notes and ledgers"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: terms.suppliersLabel },
        ]}
        action={
          canCreate("suppliers") || canCreate("purchases") || canRead("suppliers") || canRead("reports") ? (
            <Stack direction="row" spacing={1}>
              {(canRead("suppliers") || canRead("reports")) && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={exporting}
                  onClick={() => void handleTallyExport()}
                >
                  Tally export
                </Button>
              )}
              {(canCreate("suppliers") || canCreate("purchases")) ? (
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  {terms.supplierAddButton}
                </Button>
              ) : null}
            </Stack>
          ) : undefined
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message || "Failed to load suppliers"}
        </Alert>
      )}

      <DataListCard
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search name, phone, GSTIN…"
        filters={
          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setPage(0);
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        }
      >
        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>GSTIN / Tax ID</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>Loading…</TableCell>
                </TableRow>
              ) : (data?.data || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No suppliers yet.</TableCell>
                </TableRow>
              ) : (
                (data?.data || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.phone || "—"}</TableCell>
                    <TableCell>{row.taxId || "—"}</TableCell>
                    <TableCell>{row.city || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isActive ? "Active" : "Inactive"}
                        color={row.isActive ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {(canUpdate("suppliers") || canUpdate("purchases")) && (
                          <TableActionButton
                            title="Edit"
                            variant="primary"
                            onClick={() => openEdit(row)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {(canDelete("suppliers") || canDelete("purchases")) && (
                          <TableActionButton
                            title="Delete"
                            variant="danger"
                            onClick={async () => {
                              const ok = await confirm({
                                title: "Delete supplier",
                                message: `Delete supplier "${row.name}"? This cannot be undone.`,
                                confirmLabel: "Delete",
                                tone: "danger",
                              });
                              if (ok) remove(row.id);
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total || 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </DataListCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {editing ? `Edit ${terms.supplierSingular}` : terms.supplierAddButton}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact person"
                value={form.contactPerson || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, contactPerson: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone || ""}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={form.email || ""}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GSTIN / Tax ID"
                value={form.taxId || ""}
                onChange={(e) =>
                  setForm((p) => ({ ...p, taxId: e.target.value.toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={form.city || ""}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={form.state || ""}
                onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                minRows={2}
                value={form.address || ""}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive !== false}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, isActive: e.target.checked }))
                    }
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
};

export default SuppliersPage;
